import { useRents } from '@services/contracts/queires/useRents'
import { SNA_IMAGE_PREFIX } from '@services/utils'
import {
  ArrowRight,
  CalendarClock,
  CircleCheck,
  Clock,
  MapPin,
  Wallet
} from 'lucide-react'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { ContractList } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-display text-lg font-bold leading-tight text-ink">
        {children}
      </div>
    </div>
  )
}

export function ContractSummaryCard({ contract }: { contract?: ContractList }) {
  const { t } = useTranslation('contract')
  const { data: rents } = useRents()

  if (!contract) return null

  const offer = contract.offer
  const isSigned =
    contract.status === 'completed' || contract.status === 'signed'

  const unpaid = (rents?.results ?? []).filter((r) => !r.paid)
  const nextRent = [...unpaid].sort(
    (a, b) =>
      new Date(a.due_date ?? 0).getTime() - new Date(b.due_date ?? 0).getTime()
  )[0]

  return (
    <Card className="flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
      <Link
        to={`/user/offer/${offer?.id}`}
        className="group flex min-w-0 items-center gap-4"
      >
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-ink/[0.04]">
          <img
            src={`${SNA_IMAGE_PREFIX}${offer?.flat?.main_image}`}
            alt={offer?.flat?.title ?? 'Flat'}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => {
              e.currentTarget.style.visibility = 'hidden'
            }}
          />
        </div>
        <div className="min-w-0">
          <div className="truncate font-display text-base font-bold text-ink transition-colors group-hover:text-primary">
            {offer?.flat?.title}
          </div>
          {offer?.flat?.location_name && (
            <div className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
              {offer.flat.location_name}
            </div>
          )}
        </div>
      </Link>

      <div className="flex flex-wrap items-center gap-x-8 gap-y-4 lg:shrink-0">
        <Stat label={t('hero.monthlyRent')}>
          <span className="inline-flex items-center gap-1.5">
            <Wallet className="h-4 w-4 text-primary" />
            {offer?.monthly_rent_sum} €
          </span>
        </Stat>
        <Stat label={t('hero.nextPayment')}>
          <span className="inline-flex items-center gap-1.5">
            <CalendarClock className="h-4 w-4 text-primary" />
            {nextRent?.due_date
              ? new Date(nextRent.due_date).toLocaleDateString('sk-SK')
              : t('hero.noPayment')}
          </span>
        </Stat>
        <Stat label={t('hero.contractStatus')}>
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-sm font-semibold',
              isSigned
                ? 'bg-primary/15 text-primary'
                : 'bg-peach/30 text-warning'
            )}
          >
            {isSigned ? (
              <CircleCheck className="h-4 w-4" />
            ) : (
              <Clock className="h-4 w-4" />
            )}
            {isSigned ? t('hero.signed') : t('hero.created')}
          </span>
        </Stat>

        <Button asChild className="lg:ml-2">
          <Link to="/user/contract">
            {t('rent.view', { ns: 'userDashboard' })}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </Card>
  )
}
